/*
 * Calibre interactions. Progressive enhancement only: every section is usable without JS.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root = document) {
    const items = root.querySelectorAll('[data-cal-reveal]:not(.is-visible)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  }

  /* ---------- In view (plays card illustrations once, see calibre.css) ---------- */
  function initInView(root = document) {
    const items = root.querySelectorAll('[data-cal-inview]:not(.is-inview)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.55 }
    );
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Timer dial ---------- */
  // Served showing "continuous". With motion allowed it resets to 2 hours, then steps through
  // 4, 8 and continuous once when it comes into view (under five seconds). Tapping an option
  // stops the sequence and shows that option.
  function initTimers(root = document) {
    root.querySelectorAll('[data-cal-timer]').forEach((timer) => {
      if (timer.dataset.calReady) return;
      timer.dataset.calReady = 'true';
      const options = Array.from(timer.querySelectorAll('[data-cal-timer-option]'));
      const value = timer.querySelector('[data-cal-timer-value]');
      const unit = timer.querySelector('[data-cal-timer-unit]');
      if (!options.length) return;
      let steps = [];
      let io;

      const show = (btn) => {
        timer.dataset.value = btn.dataset.calTimerOption;
        options.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        if (value) value.textContent = btn.dataset.readout;
        if (unit) unit.textContent = btn.dataset.unit;
      };

      options.forEach((btn) =>
        btn.addEventListener('click', () => {
          io?.disconnect();
          steps.forEach(clearTimeout);
          steps = [];
          show(btn);
        })
      );

      if (reduceMotion || !('IntersectionObserver' in window)) return;

      timer.classList.add('is-instant');
      show(options[0]);
      timer.getBoundingClientRect();
      timer.classList.remove('is-instant');

      io = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          steps = options.slice(1).map((btn, i) => setTimeout(() => show(btn), 1100 * (i + 1)));
        },
        { threshold: 0.6 }
      );
      io.observe(timer);
    });
  }

  /* ---------- Feature videos ---------- */
  // With autoplay on, a video plays muted while at least half of it is on screen and pauses when
  // it scrolls away. Once a visitor pauses it, it stays paused until they press play again.
  // Visitors who prefer reduced motion get the poster and native controls only.
  function initVideos(root = document) {
    root.querySelectorAll('[data-cal-video][data-autoplay]').forEach((wrap) => {
      if (wrap.dataset.calReady) return;
      wrap.dataset.calReady = 'true';
      const video = wrap.querySelector('video');
      if (!video) return;
      let userPaused = false;
      let autoPausing = false;
      const sound = wrap.querySelector('[data-cal-video-sound]');
      if (sound) {
        const syncSound = () => {
          const on = !video.muted;
          sound.setAttribute('aria-pressed', on ? 'true' : 'false');
          sound.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
        };
        sound.addEventListener('click', () => {
          video.muted = !video.muted;
          if (!video.muted && video.paused) {
            userPaused = false;
            video.play().catch(() => {});
          }
          syncSound();
        });
        video.addEventListener('volumechange', syncSound);
        syncSound();
      }
      const toggle = wrap.querySelector('[data-cal-video-toggle]');
      if (toggle) {
        const sync = () => {
          const paused = video.paused;
          toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
          toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        };
        toggle.addEventListener('click', () => {
          if (video.paused) {
            userPaused = false;
            video.play().catch(() => {});
          } else {
            userPaused = true;
            video.pause();
          }
        });
        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);
        sync();
      }
      if (reduceMotion || !('IntersectionObserver' in window)) {
        if (reduceMotion) video.setAttribute('controls', '');
        return;
      }
      video.muted = true;
      let inView = false;
      // Only a visitor's pause sticks: the pause button (above), or the video itself when it has
      // native controls. Pauses the browser makes on its own (a hidden tab, low-power mode) must not
      // keep the film stopped once it is back on screen.
      video.addEventListener('pause', () => {
        if (video.controls && !autoPausing && !video.ended) userPaused = true;
        autoPausing = false;
      });
      video.addEventListener('play', () => {
        userPaused = false;
      });
      const resume = () => {
        if (inView && !userPaused && video.paused && document.visibilityState === 'visible') video.play().catch(() => {});
      };
      document.addEventListener('visibilitychange', resume);
      const io = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          if (inView) {
            resume();
          } else if (!video.paused) {
            autoPausing = true;
            video.pause();
          }
        },
        { threshold: 0.5 }
      );
      io.observe(wrap);
    });
  }

  /* ---------- Sticky local nav ---------- */
  function initLocalNav(root = document) {
    root.querySelectorAll('[data-cal-local-nav]').forEach((nav) => {
      if (!('IntersectionObserver' in window)) return;
      // The sticky element is the section wrapper; watch a 1px marker placed just before it.
      const host = nav.closest('.shopify-section') || nav;
      let sentinel = host.previousElementSibling;
      if (!sentinel || !sentinel.classList.contains('cal-sentinel')) {
        sentinel = document.createElement('div');
        sentinel.className = 'cal-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        host.before(sentinel);
      }
      const io = new IntersectionObserver(([entry]) => {
        nav.classList.toggle('is-stuck', !entry.isIntersecting);
      });
      io.observe(sentinel);
    });
  }

  /* ---------- Carousel ---------- */
  function initCarousels(root = document) {
    root.querySelectorAll('[data-cal-carousel]').forEach((wrap) => {
      if (wrap.dataset.calReady) return;
      wrap.dataset.calReady = 'true';
      const track = wrap.querySelector('[data-cal-track]');
      const prev = wrap.querySelector('[data-cal-prev]');
      const next = wrap.querySelector('[data-cal-next]');
      const dots = Array.from(wrap.querySelectorAll('[data-cal-dot]'));
      if (!track) return;
      const step = () => {
        const card = track.firstElementChild;
        if (!card) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        const index = Math.round(track.scrollLeft / step());
        dots.forEach((d, i) => d.classList.toggle('is-active', i === Math.min(index, dots.length - 1)));
      };
      const behavior = reduceMotion ? 'auto' : 'smooth';
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Sleep mode toggle ---------- */
  function initSleep(root = document) {
    root.querySelectorAll('[data-cal-sleep]').forEach((section) => {
      if (section.dataset.calReady) return;
      section.dataset.calReady = 'true';
      const buttons = section.querySelectorAll('[data-cal-sleep-mode]');
      const caption = section.querySelector('[data-cal-sleep-caption]');
      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const mode = btn.dataset.calSleepMode;
          section.dataset.mode = mode;
          buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          if (caption) caption.textContent = btn.dataset.caption || '';
        });
      });
    });
  }

  /* ---------- Controls simulator (follows the supplier's button map) ---------- */
  function initControls(root = document) {
    root.querySelectorAll('[data-cal-controls]').forEach((panel) => {
      if (panel.dataset.calReady) return;
      panel.dataset.calReady = 'true';
      const leds = {};
      panel.querySelectorAll('[data-led]').forEach((el) => (leds[el.dataset.led] = el));
      const status = panel.querySelector('[data-cal-status]');
      const note = panel.querySelector('[data-cal-note]');
      const btn = (name) => panel.querySelector(`[data-key="${name}"]`);
      const t = (key, fallback) => panel.dataset[key] || fallback;

      const LIGHTS = ['soft', 'strong', 'off'];
      const TIMERS = ['2', '4', '8', 'cont'];
      let state = { mist: 'off', light: 'off', timer: 'cont', lightIdx: -1, timerIdx: -1, sleepSettled: false };
      let sleepTimer;
      let flashTimer;

      const labels = {
        mist: { high: t('high', 'High mist'), low: t('low', 'Low mist'), sleep: t('sleep', 'Sleep mode'), off: t('off', 'Off') },
        light: { soft: t('soft', 'Soft light'), strong: t('strong', 'Strong light'), off: t('lightOff', 'Light off') },
        timer: { 2: t('timer2', '2-hour timer'), 4: t('timer4', '4-hour timer'), 8: t('timer8', '8-hour timer'), cont: t('continuous', 'Runs continuously') },
      };

      function render() {
        const running = state.mist === 'high' || state.mist === 'low';
        const on = (id, v) => leds[id] && leds[id].classList.toggle('is-on', v);
        on('on', running && state.timer === 'cont');
        on('2h', running && state.timer === '2');
        on('4h', running && state.timer === '4');
        on('8h', running && state.timer === '8');
        on('high', state.mist === 'high');
        on('low', state.mist === 'low');
        on('sleep', state.mist === 'sleep');
        if (state.mist === 'sleep' && !state.sleepSettled) on('on', true);
        panel.dataset.light = running ? state.light : 'off';
        ['light', 'timer'].forEach((k) => btn(k)?.setAttribute('aria-disabled', String(!running)));

        let text;
        if (state.mist === 'off') text = labels.mist.off;
        else if (state.mist === 'sleep') text = labels.mist.sleep;
        else text = [labels.mist[state.mist], labels.light[state.light], labels.timer[state.timer]].join(' · ');
        if (status) status.textContent = text;
      }

      function say(msg) {
        if (note) note.textContent = msg || '';
      }

      function setMist() {
        clearTimeout(sleepTimer);
        const order = ['off', 'high', 'low', 'sleep'];
        const next = order[(order.indexOf(state.mist) + 1) % order.length];
        state.mist = next;
        state.lightIdx = -1;
        state.timerIdx = -1;
        state.timer = 'cont';
        state.light = next === 'high' || next === 'low' ? 'strong' : 'off';
        state.sleepSettled = false;
        say('');
        if (next === 'sleep') {
          say(t('sleepNote', 'After 3 seconds, every light goes out except Sleep.'));
          sleepTimer = setTimeout(() => {
            state.sleepSettled = true;
            render();
          }, 3000);
        }
        render();
      }

      function setLight() {
        if (state.mist !== 'high' && state.mist !== 'low') {
          say(t('lightNote', 'The light works in High and Low modes.'));
          return;
        }
        state.lightIdx = (state.lightIdx + 1) % LIGHTS.length;
        state.light = LIGHTS[state.lightIdx];
        say('');
        render();
      }

      function setTimer() {
        if (state.mist !== 'high' && state.mist !== 'low') {
          say(t('timerNote', 'The timer works in High and Low modes.'));
          return;
        }
        state.timerIdx = (state.timerIdx + 1) % TIMERS.length;
        state.timer = TIMERS[state.timerIdx];
        say('');
        render();
      }

      btn('mist')?.addEventListener('click', setMist);
      btn('light')?.addEventListener('click', setLight);
      btn('timer')?.addEventListener('click', setTimer);

      panel.querySelector('[data-cal-dry]')?.addEventListener('click', () => {
        clearTimeout(sleepTimer);
        clearTimeout(flashTimer);
        if (state.mist === 'off') {
          state.mist = 'high';
          state.light = 'strong';
          state.timer = 'cont';
          render();
        }
        panel.dataset.flash = 'true';
        say(t('dryNote', 'Water low: the light flashes for 30 seconds, then Avant switches itself off.'));
        flashTimer = setTimeout(() => {
          panel.dataset.flash = 'false';
          state = { mist: 'off', light: 'off', timer: 'cont', lightIdx: -1, timerIdx: -1, sleepSettled: false };
          render();
        }, reduceMotion ? 1200 : 3000);
      });

      render();
    });
  }

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, n) {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }

  function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  }

  function formatRange(a, b) {
    const m = new Intl.DateTimeFormat('en-US', { month: 'short' });
    const day = (d) => d.getDate();
    if (a.getMonth() === b.getMonth()) return `${m.format(a)} ${day(a)}–${day(b)}`;
    return `${m.format(a)} ${day(a)} – ${m.format(b)} ${day(b)}`;
  }

  function initDelivery(root = document) {
    root.querySelectorAll('[data-cal-delivery]').forEach((el) => {
      const d = el.dataset;
      const now = new Date();
      const min = addDays(addBusinessDays(now, Number(d.processingMin || 1)), Number(d.transitMin || 4));
      const max = addDays(addBusinessDays(now, Number(d.processingMax || 3)), Number(d.transitMax || 9));
      const target = el.querySelector('[data-cal-delivery-range]');
      if (target) target.textContent = formatRange(min, max);
    });
  }

  /* ---------- Copy discount code ---------- */
  function initCopy(root = document) {
    root.querySelectorAll('[data-cal-copy]').forEach((btn) => {
      if (btn.dataset.calReady) return;
      btn.dataset.calReady = 'true';
      btn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(btn.dataset.calCopy);
          const original = btn.textContent;
          btn.textContent = btn.dataset.copied || 'Copied';
          setTimeout(() => (btn.textContent = original), 1800);
        } catch (e) {
          /* Clipboard unavailable: the code stays visible to copy by hand. */
        }
      });
    });
  }

  /* ---------- Welcome offer prompt ---------- */
  const store = {
    get(k) {
      try { return window.localStorage.getItem(k); } catch (e) { return null; }
    },
    set(k, v) {
      try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ }
    },
  };

  function initWelcome() {
    const el = document.querySelector('[data-cal-welcome]');
    if (!el) return;
    const KEY = 'calibre-welcome';
    const open = () => el.classList.add('is-open');
    const close = () => {
      el.classList.remove('is-open');
      store.set(KEY, 'dismissed:' + Date.now());
    };
    el.querySelector('[data-cal-welcome-close]')?.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && el.classList.contains('is-open')) close();
    });

    // After a signup Shopify reloads the page at the submitted form's anchor.
    const fromPrompt = window.location.hash === '#CalWelcomeForm';
    if (el.dataset.state === 'success') {
      store.set(KEY, 'subscribed');
      if (fromPrompt) open();
      return;
    }
    if (el.dataset.state === 'error' && fromPrompt) {
      open();
      return;
    }
    if (document.querySelector('[data-cal-signup-success]')) {
      store.set(KEY, 'subscribed');
      return;
    }
    const saved = store.get(KEY) || '';
    if (saved === 'subscribed') return;
    if (saved.startsWith('dismissed:')) {
      const days = (Date.now() - Number(saved.split(':')[1])) / 864e5;
      if (days < Number(el.dataset.suppressDays || 14)) return;
    }
    if (/\/(cart|checkout|account)/.test(window.location.pathname)) return;

    const delay = Number(el.dataset.delay || 30) * 1000;
    const minDelay = 12000;
    const start = Date.now();
    let shown = false;
    // Horizon scrolls .page-wrapper (not the window) from 990px up.
    const wrapper = document.querySelector('.page-wrapper');
    const depth = () => {
      const s = wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 ? wrapper : document.scrollingElement;
      return (s.scrollTop + s.clientHeight) / s.scrollHeight;
    };
    const onScroll = () => {
      if (depth() > 0.6 && Date.now() - start > minDelay) show();
    };
    const show = () => {
      if (shown) return;
      shown = true;
      window.removeEventListener('scroll', onScroll);
      wrapper?.removeEventListener('scroll', onScroll);
      open();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    wrapper?.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(show, delay);
  }

  /* ---------- Tumble card visuals (snippets/calibre-tumble-visual.liquid) ---------- */
  // Each demo plays once when most of it is on screen; visitors can replay, press or tap.
  // Steppers (fit, care, characters, units) show the clicked option at once and stop any auto-play.
  function onceInView(el, fn, threshold = 0.6) {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        fn();
      },
      { threshold }
    );
    io.observe(el);
  }

  function initTumbleCards(root = document) {
    root.querySelectorAll('[data-cal-anim]').forEach((wrap) => {
      if (wrap.dataset.calAnimReady) return;
      wrap.dataset.calAnimReady = 'true';
      const play = () => {
        wrap.classList.remove('is-playing');
        void wrap.getBoundingClientRect();
        wrap.classList.add('is-playing');
        wrap.dispatchEvent(new CustomEvent('cal:play'));
      };
      wrap.querySelector('[data-cal-replay]')?.addEventListener('click', play);
      if (!reduceMotion) onceInView(wrap, play);
    });

    root.querySelectorAll('[data-cal-squeeze]').forEach((btn) => {
      if (btn.dataset.calReady) return;
      btn.dataset.calReady = 'true';
      let auto;
      const press = () => {
        clearTimeout(auto);
        btn.classList.add('is-pressed');
      };
      const release = () => btn.classList.remove('is-pressed');
      btn.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        press();
        btn.setPointerCapture?.(e.pointerId);
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((t) => btn.addEventListener(t, release));
      btn.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          press();
        }
      });
      btn.addEventListener('keyup', release);
      btn.addEventListener('blur', release);
      btn.addEventListener('contextmenu', (e) => e.preventDefault());
      if (!reduceMotion) {
        onceInView(btn, () => {
          auto = setTimeout(() => {
            btn.classList.add('is-pressed');
            auto = setTimeout(release, 750);
          }, 500);
        });
      }
    });

    root.querySelectorAll('[data-cal-stepper]').forEach((wrap) => {
      if (wrap.dataset.calStepReady) return;
      wrap.dataset.calStepReady = 'true';
      const options = Array.from(wrap.querySelectorAll('[data-cal-step]'));
      const panels = Array.from(wrap.querySelectorAll('[data-cal-step-panel]'));
      const label = wrap.querySelector('[data-cal-step-label]');
      const text = wrap.querySelector('[data-cal-step-text]');
      const read = wrap.querySelector('[data-cal-weigh-read]');
      if (!options.length) return;
      let timers = [];
      let counting = 0;
      const stop = () => {
        timers.forEach(clearTimeout);
        timers = [];
        cancelAnimationFrame(counting);
      };
      const reading = (btn, value) =>
        `${btn.dataset.unit === 'g' ? Math.round(value) : value.toFixed(1)} ${btn.dataset.unit}`;

      const show = (i, stepped = true) => {
        const btn = options[i];
        wrap.dataset.step = String(i);
        options.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.calStepPanel === String(i)));
        if (label && btn.dataset.label) label.textContent = btn.dataset.label;
        if (text && btn.dataset.text) text.textContent = btn.dataset.text;
        if (btn.dataset.halo) wrap.style.setProperty('--halo', btn.dataset.halo);
        if (read) read.textContent = reading(btn, parseFloat(btn.dataset.value));
        if (stepped) {
          wrap.classList.remove('is-stepped');
          void wrap.getBoundingClientRect();
          wrap.classList.add('is-stepped');
        }
      };

      options.forEach((btn, i) =>
        btn.addEventListener('click', () => {
          stop();
          show(i);
        })
      );

      // The scale counts up to the reading when the cushion lands.
      if (read) {
        wrap.addEventListener('cal:play', () => {
          stop();
          const btn = options.find((b) => b.getAttribute('aria-pressed') === 'true') || options[0];
          const target = parseFloat(btn.dataset.value);
          read.textContent = reading(btn, 0);
          timers.push(
            setTimeout(() => {
              const start = performance.now();
              const tick = (now) => {
                const k = Math.min(1, (now - start) / 700);
                read.textContent = reading(btn, target * (1 - Math.pow(1 - k, 3)));
                if (k < 1) counting = requestAnimationFrame(tick);
              };
              counting = requestAnimationFrame(tick);
            }, 520)
          );
        });
      }

      if (!('calAuto' in wrap.dataset) || reduceMotion || !('IntersectionObserver' in window)) return;
      // Auto-play: start from the first option and step through to where the markup started
      // (or all the way round and back to the first, for a loop).
      const end = Number(wrap.dataset.step || 0);
      const order = 'calLoop' in wrap.dataset
        ? options.map((_, i) => (i + 1) % options.length)
        : options.map((_, i) => i).slice(1, end + 1);
      if ('calLoop' in wrap.dataset || end > 0) {
        wrap.classList.add('is-instant');
        show(0, false);
        void wrap.getBoundingClientRect();
        wrap.classList.remove('is-instant');
      }
      onceInView(wrap, () => {
        const gap = 'calLoop' in wrap.dataset ? 1300 : 1100;
        timers = order.map((i, n) => setTimeout(() => show(i), gap * (n + 1)));
      });
    });
  }

  function init(root = document) {
    initReveal(root);
    initInView(root);
    initTimers(root);
    initTumbleCards(root);
    initVideos(root);
    initLocalNav(root);
    initCarousels(root);
    initSleep(root);
    initControls(root);
    initDelivery(root);
    initCopy(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initWelcome();
    });
  } else {
    init();
    initWelcome();
  }

  // Re-initialise sections when edited in the theme editor.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
